import sileo from 'sileo';
import axios from 'source/lib/axios';

const MilestoneDetails = new sileo.Model('hqzen', 'milestone-details');
const SimpleMilestoneDetails = new sileo.Model('hqzen', 'simple-milestone-details');
const MilestoneCards = new sileo.Model('hqzen', 'milestone-cards');
const MilestoneMembers = new sileo.Model('hqzen', 'milestone-members');
const NewMilestoneMembers = new sileo.Model('hqzen', 'new-milestone-members');
const MilestoneMemberTaskAssignments = new sileo.Model('hqzen', 'milestone-member-task-assignments');
const Milestone = new sileo.Model('hqzen', 'milestone');
const MilestoneDocument = new sileo.Model('hqzen', 'milestone-attachments');
const MilestoneDocumentRequest = new sileo.Model('hqzen', 'milestone-attachment-requests');
const MilestoneAttachmentAmazonCredentials = new sileo.Model('hqzen', 'milestone-attachment-amazon');
const MilestoneCommentAttachmentAmazonCredentials = new sileo.Model('hqzen',
    'milestone-comment-attachment-amazon-form-data');
const MilestoneAttachmentAcknowledgements = new sileo.Model('hqzen', 'milestone-attachment-acknowledgement');
const MilestoneComment = new sileo.Model('hqzen', 'milestone-comment');
const MilestoneCardRequests = new sileo.Model('hqzen', 'milestone-card-requests');
const MilestoneSubtask = new sileo.Model('hqzen', 'milestone-subtasks');
const MilestonePendingRequest = new sileo.Model('hqzen', 'milestone-pending-requests');
const MilestoneChangeLogActivity = new sileo.Model('hqzen', 'milestone-changelog-activity');
const UpdateMultipleMilestoneSubtask = new sileo.Model('hqzen', 'update-multiple-milestone-subtask');
const MilestoneSummary = new sileo.Model('hqzen', 'milestone-summary');
const MilestoneMemberCheck = new sileo.Model('hqzen', 'milestone-member-check');

// Milestone Daily Progress
const MilestoneSubtasksDailyProgress = new sileo.Model('hqzen', 'milestone-subtasks-daily-progress');
const MilestoneCardsDailyProgress = new sileo.Model('hqzen', 'milestone-cards-daily-progress');
const MilestoneContributorsDailyProgress = new sileo.Model('hqzen', 'milestone-contributors-daily-progress');

const state = {
    selectedAuthors: [],
    viewedMilestoneDetails: null
};

const getters = {
};

const mutations = {
    setSelectedAuthors(state, data) {
        state.selectedAuthors = data;
    },

    setViewedMilestoneDetails(state, data) {
        state.viewedMilestoneDetails = data;
    },
};

const actions = {
    async milestoneMemberCheck(context, pk) {
        try {
            const details = await MilestoneMemberCheck.objects.get(pk);
            return details;
        } catch (error) {
            throw error;
        }
    },

    async getMilestoneDetails(context, pk) {
        try {
            const details = await MilestoneDetails.objects.get(pk);
            context.commit('setViewedMilestoneDetails', details);
            return details;
        } catch (error) {
            throw error;
        }
    },

    async getSimpleMilestoneDetails(context, pk) {
        try {
            const details = await SimpleMilestoneDetails.objects.get(pk);
            context.commit('setViewedMilestoneDetails', details);
            return details;
        } catch (error) {
            throw error;
        }
    },

    getMilestoneCard(context, pk) {
        return MilestoneCards.objects.get(pk);
    },

    fetchMilestoneCardRequest(context, filters) {
        return MilestoneCardRequests.objects.filter(filters);
    },

    fetchMilestonePendingRequest(context, filters) {
        return MilestonePendingRequest.objects.filter(filters);
    },

    fetchMilestoneCards(context, filters) {
        return MilestoneCards.objects.filter(filters);
    },

    fetchMilestoneMembers(context, filters) {
        return MilestoneMembers.objects.filter(filters);
    },

    fetchNewMilestoneMembers(context, filters) {
        return NewMilestoneMembers.objects.filter(filters);
    },

    fetchMilestoneMemberTaskAssignments(context, filters) {
        return MilestoneMemberTaskAssignments.objects.filter(filters);
    },

    fetchMilestones(context, filters) {
        return Milestone.objects.filter(filters);
    },

    fetchMilestoneChangeLogActivity(context, filters) {
        return MilestoneChangeLogActivity.objects.filter(filters);
    },

    createMilestone(context, payload) {
        const form = new FormData();
        for (let key in payload) {
            if (key === 'leads' || key === 'members' ||
                key === 'estimation_question_sets') {
                form.append(key, JSON.stringify(payload[key]));
            } else {
                form.append(key, payload[key]);
            }
        }
        return Milestone.objects.create(form);
    },

    async updateMilestone(context, data) {
        const form = new FormData();
        let pk = data.pk;

        // serialize once, outside loop
        if (data.leads) {
            data.leads = JSON.stringify(data.leads);
        }

        if (data.members) {
            data.members = JSON.stringify(data.members);
        }

        if (data.estimation_question_sets) {
            data.estimation_question_sets = JSON.stringify(data.estimation_question_sets);
        }

        // safely append
        for (let field in data) {
            form.append(field, data[field]);
        }

        return Milestone.objects.update({ pk }, form);
    },

    updateMilestoneCardRequest(context, { pk, data }) {
        const form = new FormData();

        for (let key in data) {
            form.append(key, data[key]);
        }
        return MilestoneCardRequests.objects.update({ 'pk': pk }, data);
    },

    fetchMilestoneComments(context, filters) {
        return MilestoneComment.objects.filter(filters);
    },

    createMilestoneComment(context, data) {
        let form = new FormData();

        for (let [key, value] of Object.entries(data)) {
            form.append(key, value);
        }

        return MilestoneComment.objects.create(form);
    },

    updateMilestoneComment(context, data) {
        const form = new FormData();
        const object_id = data['pk'];
        delete data['pk'];

        for (const field in data) {
            form.append(field, data[field]);
        }

        return MilestoneComment.objects.update({ 'pk': object_id }, form);
    },

    deleteMilestoneComment(context, data) {
        const object_id = data['pk'];
        return MilestoneComment.objects.delete({ 'pk': object_id });
    },

    fetchMilestoneDocuments(context, filters) {
        return MilestoneDocument.objects.filter(filters);
    },

    async fetchMilestoneDocumentAmazonFormData(context, filters) {
        return await MilestoneAttachmentAmazonCredentials.objects.filter(filters).then(data => {
            return data;
        }).catch(error => {
            console.error(error);
            return null;
        });
    },

    async fetchMilestoneCommentAttachmentAmazonFormData(context, content_type) {
        return await MilestoneCommentAttachmentAmazonCredentials.objects.filter(
            { content_type: content_type }).then(amazon => {
            return amazon.amazon;
        });
    },

    createMilestoneDocument(context, form) {
        return MilestoneDocument.objects.create(form);
    },

    createMilestoneDocumentRequest(context, form) {
        return MilestoneDocumentRequest.objects.create(form);
    },

    updateMilestoneDocument(context, { pk, data, isDeleteDocRequest=false }) {
        return MilestoneDocument.objects.update({ 'pk': pk }, data);
    },

    updateMilestoneDocumentRequestStatus(context, { pk, data }) {
        const form = new FormData();

        for (let key in data) {
            form.append(key, data[key]);
        }
        return MilestoneDocumentRequest.objects.update({ 'pk': pk }, form);
    },

    createDocumentAcknowledgement(context, data) {
        const form = new FormData();

        for (let key in data) {
            form.append(key, data[key]);
        }

        return MilestoneAttachmentAcknowledgements.objects.create(form);
    },

    updateDocumentAcknowledgement(context, { pk, data }) {
        const form = new FormData();

        for (let key in data) {
            form.append(key, data[key]);
        }
        return MilestoneAttachmentAcknowledgements.objects.update({ 'pk': pk }, form);
    },

    fetchMilestoneSubtasks(context, filters) {
        return MilestoneSubtask.objects.filter(filters);
    },

    getMilestoneSubtask(context, pk) {
        return MilestoneSubtask.objects.get(pk);
    },

    createMilestoneSubtask(context, data) {
       const form = new FormData();
       for (let key in data) {
        form.append(key, data[key]);
       }
    return MilestoneSubtask.objects.create(form);
    },

    updateMilestoneSubtask(context, { task__pk, data }) {
        const form = new FormData();
        for (let key in data) {
         form.append(key, data[key]);
        }
        if (data['toBeDeleted'] === true) {
            delete data['toBeDeleted'];
            return MilestoneSubtask.objects.delete({ 'task__pk': task__pk });
        }
        return MilestoneSubtask.objects.update({ 'task__pk': task__pk }, form);
    },

    downloadMilestonesViewExportCSV(context, params) {
        const url = window.location.pathname;
        return axios.get(url+'/export/csv', {
            params: params
        });
    },

    fetchMilestoneSummary(context, pk) {
        return MilestoneSummary.objects.get(pk);
    },

    updateMilestoneSummary(context, filters) {
        return MilestoneSummary.objects.update(filters);
    },

    updateMultipleMilestoneSubtask(context, filters) {
        return UpdateMultipleMilestoneSubtask.objects.update(filters);
    },

    fetchMilestoneSubtasksDailyProgress(context, filters) {
        return MilestoneSubtasksDailyProgress.objects.filter(filters);
    },

    fetchMilestoneCardsDailyProgress(context, filters) {
        return MilestoneCardsDailyProgress.objects.filter(filters);
    },

    fetchMilestoneContributorsDailyProgress(context, filters) {
        return MilestoneContributorsDailyProgress.objects.filter(filters);
    },

};


export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
